import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import API_ROUTES from "../../config/apiRoutes";
import TicketsTopBar from "../../components/Tickets/TicketsTopBar";
import TicketsTable from "../../components/Tickets/TicketsTable";
import TicketDetailModal from "../../components/Tickets/TicketDetailModal";
import { useDispatch } from "react-redux";
import { showNotification } from "../../redux/slices/notificationSlice";

const ITEMS_PER_PAGE = 10;

export default function TicketingPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useDispatch();

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };

      // Add filters if they are not 'all'
      if (statusFilter && statusFilter !== "all") {
        payload.ticketStatus = statusFilter;
      }

      if (categoryFilter && categoryFilter !== "all") {
        payload.category = categoryFilter;
      }

      if (searchTerm && searchTerm.trim()) {
        payload.search = searchTerm.trim();
      }

      console.log("Fetching tickets with payload:", payload);
      const response = await axios.post(API_ROUTES.TICKETS.GET_ALL, payload);
      
      console.log("Tickets response:", response.data);
      if (response.data) {
        setTickets(response.data.data || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);
        setError(null);
      }
    } catch (err) {
      console.error("Failed to fetch tickets:", err);
      setError("Failed to fetch tickets.");
      dispatch(
        showNotification({
          message: err.response?.data?.message || "Failed to fetch tickets",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, categoryFilter, searchTerm, dispatch]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleCategoryChange = (value) => {
    setCategoryFilter(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  const handleReplyAdded = () => {
    // Refresh tickets list after reply is added
    fetchTickets();
  };

  return (
    <>
      <div className="bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
        <TicketsTopBar
          total={total}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          statusFilter={statusFilter}
          onStatusChange={handleStatusChange}
          categoryFilter={categoryFilter}
          onCategoryChange={handleCategoryChange}
        />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dashboardCoral"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-red-500">{error}</p>
          </div>
        ) : (
          <TicketsTable
            tickets={tickets}
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
            onTicketClick={handleTicketClick}
          />
        )}
      </div>

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        ticket={selectedTicket}
        onReplyAdded={handleReplyAdded}
      />
    </>
  );
}
